/**
 * The single owner of every TanStack Query key used in `apps/ui`.
 *
 * Why this module exists: before it, a hook that needed to invalidate another
 * hook's query had to copy that hook's private key literal by hand. Forgetting
 * a copy is a silent failure -- the mutation succeeds server-side and the card
 * stays stale until a `refetchInterval` papers over it. Nothing throws, nothing
 * fails a test. With every key behind a builder, renaming one is a compile
 * error instead.
 *
 * Rules for editing this file:
 *
 * 1. A key's serialized value is a cache contract. Changing the array a builder
 *    returns orphans every cache entry already stored under the old value, so
 *    treat a value change as a behaviour change and call it out in review.
 * 2. `all()` returns the PREFIX that TanStack matches partially, so
 *    `invalidateQueries({ queryKey: queryKeys.orders.all() })` invalidates every
 *    key below it. A builder whose root string differs from its family's root is
 *    therefore NOT covered by that family's `all()`; those cases are commented
 *    where they occur, and they are preserved exactly as they are today.
 * 3. Builders are pure and dependency-free. They take primitives, never domain
 *    types, so this module never imports from a feature and can be imported by
 *    any of them.
 */

// =============================================================================
// Orders
// =============================================================================

const ORDERS_ROOT = ["orders"] as const;

export const orderKeys = {
  all: () => ORDERS_ROOT,
  /** `filters` is only ever compared structurally by TanStack -- an absent
   * filter set must serialize as `{}`, exactly as it did before this module. */
  list: (filters?: unknown) => [...ORDERS_ROOT, "list", filters ?? {}] as const,
  detail: (orderId?: string) => [...ORDERS_ROOT, "detail", orderId] as const,
  mine: () => [...ORDERS_ROOT, "mine"] as const,
  settings: () => [...ORDERS_ROOT, "settings"] as const,
  tables: () => [...ORDERS_ROOT, "tables"] as const,
} as const;

/**
 * Order rating lives under the SINGULAR `order` root, not under `orders`, so it
 * is deliberately outside `orderKeys`: invalidating `orderKeys.all()` does not
 * touch it. Kept as-is to avoid orphaning cached ratings.
 */
export const orderRatingKeys = {
  detail: (orderId?: string) => ["order", "rating", orderId] as const,
} as const;

// =============================================================================
// Delivery (customer / staff tracking side)
// =============================================================================

const DELIVERY_ROOT = ["delivery"] as const;

export const deliveryKeys = {
  all: () => DELIVERY_ROOT,
  track: (deliveryId?: string) => [...DELIVERY_ROOT, "track", deliveryId] as const,
  byOrder: (orderId?: string) => [...DELIVERY_ROOT, "by-order", orderId] as const,
} as const;

/** Own root (`delivery-session`), not below `deliveryKeys.all()`. */
export const deliverySessionKeys = {
  byOrgSlug: (orgSlug?: string) => ["delivery-session", orgSlug] as const,
} as const;

// =============================================================================
// Rider (the Rider's own app surface)
// =============================================================================

const RIDER_ROOT = ["rider"] as const;

export const riderDeliveryKeys = {
  all: () => [...RIDER_ROOT, "deliveries"] as const,
  history: () => [...RIDER_ROOT, "deliveries", "history"] as const,
} as const;

export const riderAvailabilityKeys = {
  all: () => [...RIDER_ROOT, "availability"] as const,
} as const;

// =============================================================================
// Riders (the staff-side roster)
// =============================================================================

const RIDERS_ROOT = ["riders"] as const;

export const riderKeys = {
  all: () => RIDERS_ROOT,
  assignable: () => [...RIDERS_ROOT, "assignable"] as const,
  /** Scoped per Order; an absent Order must serialize as `null`, not as
   * `undefined`, so the unscoped list keeps its own cache entry. */
  assignableForOrder: (orderId?: string) =>
    [...RIDERS_ROOT, "assignable", orderId ?? null] as const,
} as const;

// =============================================================================
// Menu
// =============================================================================

const MENU_ROOT = ["menu"] as const;
const MENU_PROMOTIONS_ROOT = [...MENU_ROOT, "promotions"] as const;

export const menuKeys = {
  all: () => MENU_ROOT,
  sections: {
    all: () => [...MENU_ROOT, "sections"] as const,
    byOrg: (activeOrganization?: string) => [...MENU_ROOT, "sections", activeOrganization] as const,
  },
  collections: {
    all: () => [...MENU_ROOT, "collections"] as const,
    byOrg: (activeOrganization?: string) =>
      [...MENU_ROOT, "collections", activeOrganization] as const,
  },
  products: {
    all: () => [...MENU_ROOT, "products"] as const,
    byOrg: (activeOrganization?: string) => [...MENU_ROOT, "products", activeOrganization] as const,
  },
  productsView: {
    all: () => [...MENU_ROOT, "products-view"] as const,
    byOrg: (activeOrganization?: string) =>
      [...MENU_ROOT, "products-view", activeOrganization] as const,
  },
  publicMenu: {
    all: () => [...MENU_ROOT, "public"] as const,
    byOrgSlug: (orgSlug?: string) => [...MENU_ROOT, "public", orgSlug] as const,
  },
  promotions: {
    custom: {
      all: () => [...MENU_PROMOTIONS_ROOT, "custom"] as const,
      byOrg: (activeOrganization?: string) =>
        [...MENU_PROMOTIONS_ROOT, "custom", activeOrganization] as const,
    },
    section: {
      all: () => [...MENU_PROMOTIONS_ROOT, "section"] as const,
      byOrg: (activeOrganization?: string) =>
        [...MENU_PROMOTIONS_ROOT, "section", activeOrganization] as const,
    },
    collection: {
      all: () => [...MENU_PROMOTIONS_ROOT, "collection"] as const,
      byOrg: (activeOrganization?: string) =>
        [...MENU_PROMOTIONS_ROOT, "collection", activeOrganization] as const,
    },
  },
} as const;

// =============================================================================
// Storefronts
// =============================================================================

export const storefrontKeys = {
  all: () => ["storefronts"] as const,
} as const;

// =============================================================================
// Organization
//
// These come from better-auth's client and each one is its own flat root
// (`organizations`, `activeOrganization`, `teamMembers`, ...). They are grouped
// here for discoverability, NOT nested under a shared prefix, because nesting
// them would change every serialized value.
// =============================================================================

export const organizationKeys = {
  list: () => ["organizations"] as const,
  active: () => ["activeOrganization"] as const,
  members: (organizationId?: string) => ["organizationMembers", organizationId] as const,
  invitations: (organizationId?: string) => ["organizationInvitations", organizationId] as const,
  userInvitations: () => ["userInvitations"] as const,
  invitationPreview: (invitationId?: string) => ["invitationPreview", invitationId] as const,
  teams: (organizationId?: string) => ["teams", organizationId] as const,
  teamMembers: (teamId?: string) => ["teamMembers", teamId] as const,
  /** Singular `organization` root -- not matched by `list()`. */
  billingAccess: () => ["organization", "billing-access"] as const,
} as const;

export const orgSettingsKeys = {
  all: () => ["orgSettings"] as const,
  byOrg: (orgId?: string) => ["orgSettings", orgId] as const,
} as const;

// =============================================================================
// Subscriptions and billing
// =============================================================================

const SUBSCRIPTIONS_ROOT = ["subscriptions"] as const;

export const subscriptionKeys = {
  all: () => SUBSCRIPTIONS_ROOT,
  list: (organizationId?: string) => [...SUBSCRIPTIONS_ROOT, "list", organizationId] as const,
  membership: (organizationId?: string) =>
    [...SUBSCRIPTIONS_ROOT, "membership", organizationId] as const,
  products: () => [...SUBSCRIPTIONS_ROOT, "products"] as const,
  productTiers: () => [...SUBSCRIPTIONS_ROOT, "products", "tiers"] as const,
  planChangePreview: (subscriptionId?: string, targetProductId?: string, billingPeriod?: string) =>
    [
      ...SUBSCRIPTIONS_ROOT,
      "plan-change-preview",
      subscriptionId,
      targetProductId,
      billingPeriod,
    ] as const,
  /** Own root (`subscription-details`), keyed by the provider preapproval id,
   * which a caller may still hold as `null` before one is resolved. */
  details: (preapprovalId?: string | null) => ["subscription-details", preapprovalId] as const,
} as const;

/** Own root (`products`), distinct from `subscriptionKeys.products()`. */
export const productKeys = {
  all: () => ["products"] as const,
} as const;

// =============================================================================
// Account / customer
// =============================================================================

export const userSessionKeys = {
  all: () => ["user-session"] as const,
} as const;

export const customerAddressKeys = {
  all: () => ["customer-addresses"] as const,
} as const;

export const phoneVerificationKeys = {
  all: () => ["phone-verification"] as const,
} as const;

// =============================================================================
// Admin
// =============================================================================

const ADMIN_ROOT = ["admin"] as const;

export const adminKeys = {
  all: () => ADMIN_ROOT,
  stats: () => [...ADMIN_ROOT, "stats"] as const,
  userStats: () => [...ADMIN_ROOT, "user-stats"] as const,
  recentActivity: () => [...ADMIN_ROOT, "recent-activity"] as const,
  /** `query` is the admin user-list filter object, compared structurally by
   * TanStack exactly as it was before this module. */
  users: (query?: unknown) => [...ADMIN_ROOT, "users", query] as const,
  userSessions: (userId: string) => [...ADMIN_ROOT, "user", userId, "sessions"] as const,
  /** Own root (`admin-plans-sync`) -- not matched by `adminKeys.all()`. */
  plansSync: () => ["admin-plans-sync"] as const,
} as const;

// =============================================================================
// AI
// =============================================================================

export const aiKeys = {
  all: () => ["ai"] as const,
} as const;

/**
 * One namespace so a caller can reach any family without importing a dozen
 * symbols. The individual exports above stay available for narrow imports.
 */
export const queryKeys = {
  admin: adminKeys,
  ai: aiKeys,
  customerAddresses: customerAddressKeys,
  delivery: deliveryKeys,
  deliverySession: deliverySessionKeys,
  menu: menuKeys,
  orderRating: orderRatingKeys,
  orders: orderKeys,
  organizations: organizationKeys,
  orgSettings: orgSettingsKeys,
  phoneVerification: phoneVerificationKeys,
  products: productKeys,
  riderAvailability: riderAvailabilityKeys,
  riderDeliveries: riderDeliveryKeys,
  riders: riderKeys,
  storefronts: storefrontKeys,
  subscriptions: subscriptionKeys,
  userSession: userSessionKeys,
} as const;
